import { type CSSProperties, type FC, type ReactNode, useMemo } from "react";

import { ImagePreviewer } from "../imagePreview/ImagePreviewer";
import { Badge, type BadgeVariants } from "../ui/Badge";
import { Side } from "../ui/CustomChervonIcon";

import { MinimalExpandableText } from "./MinimalExpandableText";

import { InductionStatus } from "@/features/induction/induction.enums";
import { cn } from "@/utils/core/generalUtils";
import { getInductionStatusBadgeVariant } from "@/utils/domain/statusBadgeHelper";

export interface LabelValueItem {
  label: string;
  value: string | number | undefined | string[] | ReactNode;
  className?: string;
  labelItemClassName?: string;
  valueItemClassName?: string;
  isShift?: boolean;
  isStatus?: boolean;
  isImage?: boolean;
  isList?: boolean;
  shift?: string;
  projectType?: string;
  statusVariant?: BadgeVariants;
  statusStyle?: CSSProperties;
  images?: string[];
  isExpandable?: boolean;
  imageCount?: number;
  showHoverContent?: boolean;
  side?: Side;
  classes?: {
    imgClassName?: string;
    containerClassName?: string;
    badgeClassName?: string;
  };
  hoverTitle?: string;
  hide?: boolean;
}

interface LabelValuePairProps {
  data: LabelValueItem[];
  containerClassName?: string;
  labelClassName?: string;
  valueClassName?: string;
}

const LabelValuePair: FC<LabelValuePairProps> = ({
  data,
  containerClassName,
  labelClassName = "text-xs text-gray-400 w-1/2",
  valueClassName = "text-sm text-gray-700 font-medium w-1/2",
}) => {
  const filteredData = useMemo(() => data.filter((item) => !item.hide), [data]);

  const renderValue = (item: LabelValueItem): React.JSX.Element => {
    if (item.isShift) {
      return (
        <div className='flex gap-2'>
          <Badge variant='outline' className='bg-gray-50 text-gray-500'>
            {item.value}
          </Badge>
          <Badge variant='outline' className='bg-gray-50 text-gray-500'>
            {item.shift}
          </Badge>
          <Badge variant='outline' className='bg-gray-50 text-gray-500'>
            {item.projectType}
          </Badge>
        </div>
      );
    }

    if (item.isStatus) {
      const badgeVariant =
        item.statusVariant ||
        getInductionStatusBadgeVariant(item.value as InductionStatus);

      return (
        <div className={cn("flex items-center", valueClassName)}>
          {item?.value ? (
            <Badge
              variant={badgeVariant}
              className={cn(item.classes?.badgeClassName)}
              style={item.statusStyle}
            >
              {item.value}
            </Badge>
          ) : (
            "N/A"
          )}
        </div>
      );
    }

    if (item.isImage && item.images && item.images.length > 0) {
      return (
        <div className={cn("flex items-center", valueClassName)}>
          <ImagePreviewer
            imagesSrc={item.images}
            imageCount={item.imageCount || 4}
            showHoverContent={item.showHoverContent}
            side={item.side || "bottom"}
            classes={item.classes}
          />
        </div>
      );
    }

    if (item.isList && Array.isArray(item.value) && item.value.length > 0) {
      const content = (
        <ul className={cn("list-disc list-inside", valueClassName)}>
          {item.value.map((item, index) => (
            <li key={index}>{String(item)}</li>
          ))}
        </ul>
      );

      return item.isExpandable ? (
        <div
          className={cn(
            valueClassName,
            item.className,
            item.valueItemClassName,
          )}
          title={item.hoverTitle}
        >
          <MinimalExpandableText>{content}</MinimalExpandableText>
        </div>
      ) : (
        <p
          className={cn(
            valueClassName,
            item.className,
            item.valueItemClassName,
          )}
          title={item.hoverTitle}
        >
          {content}
        </p>
      );
    }

    return item.isExpandable ? (
      <div
        className={cn(valueClassName, item.className, item.valueItemClassName)}
        title={item.hoverTitle}
      >
        {item.isExpandable ? (
          <MinimalExpandableText>{item.value}</MinimalExpandableText>
        ) : (
          item.value
        )}
      </div>
    ) : (
      <p
        className={cn(valueClassName, item.className, item.valueItemClassName)}
        title={item.hoverTitle}
      >
        {item.value}
      </p>
    );
  };

  return (
    <div className={cn("flex flex-col gap-4", containerClassName)}>
      {filteredData.map((item, index) => (
        <div
          key={index}
          className={cn(
            "flex gap-2 w-full items-center",
            item.isShift && "flex-col items-start justify-start",
          )}
        >
          <p
            className={cn(
              labelClassName,
              item.className,
              item.labelItemClassName,
            )}
          >
            {item.label}
          </p>
          {renderValue(item)}
        </div>
      ))}
    </div>
  );
};

export default LabelValuePair;
